// §6.3 / §19.4 — the macOS dialog: a sheet that slides from the title bar, **Cancel then OK**.
//
// The button order is the platform's and is the thing the conformance suite checks: Cancel on the
// left, the default action on the right. It happens to match the house skin's order and to differ
// from the Windows skin's — which is exactly why the order belongs to a skin rather than to the
// shell.
//
// The focus trap is shell-owned and lives in `host/ShellUiHost`: a skin decides what a dialog looks
// like, never whether an app can escape one.
import { useEffect, useRef, useState } from 'react';
import type { DialogProps } from '@hangar/shared';
import { COPY } from '@/copy';

export function Dialog({ request, onAccept, onCancel }: DialogProps) {
  const [value, setValue] = useState(request.defaultValue ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acceptRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setValue(request.defaultValue ?? '');
    if (request.kind === 'prompt') inputRef.current?.focus();
    else acceptRef.current?.focus();
  }, [request.id, request.kind, request.defaultValue]);

  const dismissible = request.kind !== 'alert';

  return (
    <div className="macos-sheet" role="dialog" aria-modal="true" data-testid="shell-dialog">
      <p className="macos-sheet-message" data-testid="shell-dialog-message">
        {request.message}
      </p>

      {request.kind === 'prompt' ? (
        <input
          ref={inputRef}
          type="text"
          className="macos-sheet-input"
          data-testid="shell-dialog-input"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onAccept(value);
          }}
        />
      ) : null}

      {/* §6.3 — **Cancel/OK**, in that order. */}
      <div className="macos-sheet-actions">
        {dismissible ? (
          <button
            type="button"
            className="macos-sheet-button"
            data-testid="shell-dialog-cancel"
            onClick={onCancel}
          >
            {request.cancelLabel ?? COPY.chrome.cancel}
          </button>
        ) : null}
        <button
          ref={acceptRef}
          type="button"
          className="macos-sheet-button macos-sheet-button--primary"
          data-testid="shell-dialog-accept"
          onClick={() => onAccept(request.kind === 'prompt' ? value : undefined)}
        >
          {request.confirmLabel ?? COPY.chrome.ok}
        </button>
      </div>
    </div>
  );
}
